import React, { Suspense, ReactNode, useMemo, useState } from "react"
import { AppLayout } from "app/core/layouts/AppLayout"
import Link from "next/link"
import { BlitzPage } from "@blitzjs/next"
import { useSession } from "@blitzjs/auth"
import { useQuery } from "@blitzjs/rpc"
import { HiCalendar, HiCheckCircle, HiDownload, HiSearch } from "react-icons/hi"
import Loading from "app/core/components/Loading"
import { ButtonLink } from "app/core/components/ButtonLink"
import { PageHeader } from "app/core/components/PageHeader"
import getBookings from "app/bookings/queries/getBookings"
import { formatFriendlyDateWithDay, TestStatuses } from "app/orders/utils"
import { capitalize } from "lodash"
import { useCurrentUser } from "app/core/hooks/useCurrentUser"
import { Pill, PillVariant } from "app/core/components/Pill"
import { TestSubject } from "@prisma/client"
import { format } from "date-fns"
import clsx from "clsx"

import getTestCredits from "app/billing/queries/getTestCredits"
import getSubscriptionDetails from "app/orders/queries/getSubscriptionDetails"
import getStudentTestingProgress, {
  StudentTestingProgressRow,
} from "app/map-testing/queries/getStudentTestingProgress"

const SectionHeading = ({ children }: { children: ReactNode }) => {
  return (
    <div className="pb-4 border-b border-gray-200">
      <h3 className="text-lg font-bold leading-6 text-gray-900">{children}</h3>
    </div>
  )
}

export const UpcomingTesting = () => {
  const session = useSession({ suspense: false })
  const [bookingsResult] = useQuery(getBookings, {})
  const { bookingOrders = [] } = bookingsResult ?? {}

  const pillVariant = (subject: TestSubject): PillVariant => {
    switch (subject) {
      case TestSubject.READING:
        return "blue"
      case TestSubject.MATH:
        return "yellow"
      case TestSubject.LANGUAGE:
        return "green"
      case TestSubject.SCIENCE:
        return "purple"
      case TestSubject.SP_READING:
        return "amber"
      case TestSubject.SP_MATH:
        return "amber"
      default:
        throw new Error(`Invalid subject ${subject}`)
    }
  }

  return (
    <div className="space-y-8">
      {bookingOrders.length > 0 ? (
        <ul className="space-y-3 ">
          {bookingOrders.map((bookingOrder) => (
            <li className="text-black upcoming-booking-item" key={bookingOrder.id}>
              <div className="flex items-baseline space-x-3">
                <div className="text-gray-900">
                  {formatFriendlyDateWithDay(bookingOrder.booking.startAt)}
                </div>
                <div>
                  <Pill variant={pillVariant(bookingOrder.booking.subject!)}>
                    {capitalize(bookingOrder.booking.subject!)}
                  </Pill>
                </div>
                {bookingOrder.order.orderGroup &&
                  bookingOrder.order.orderGroup.organizationId === session.orgId &&
                  bookingOrder.order.tests.length === 0 && (
                    <div>
                      <Pill variant="red">Group Admin Order</Pill>
                    </div>
                  )}
                <Link
                  href={`/orders/${bookingOrder.orderId}`}
                  className="ml-1 text-xs text-gray-500 uppercase manage-link hover:text-gray-700 hover:underline"
                >
                  Manage
                </Link>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div>No tests scheduled</div>
      )}

      <div>
        <ButtonLink href="/book-testing">
          <HiCalendar className="-ml-0.5 mr-2 h-4 w-4" />
          Book Testing Now
        </ButtonLink>
        <span className="ml-2 text-sm text-gray-700">(up to 3 months out)</span>
      </div>
    </div>
  )
}

const TESTING_PROGRESS_STATUS_STYLES: Record<string, string> = {
  COMPLETE: "bg-green-100 text-green-800",
  IN_PROGRESS: "bg-blue-100 text-blue-800",
  CHECKED_IN: "bg-gray-100 text-gray-600",
  SCHEDULED: "bg-gray-100 text-gray-600",
}

const TestingProgressBadge = ({ status }: { status: string }) => (
  <span
    className={clsx(
      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
      TESTING_PROGRESS_STATUS_STYLES[status] ?? "bg-gray-100 text-gray-600"
    )}
  >
    {TestStatuses[status] ?? status}
  </span>
)

type TestingProgressSortField = "student" | "status" | "subject" | "questionNumber" | "updatedAt"

const compareNullableStrings = (a: string | null, b: string | null) =>
  (a ?? "").localeCompare(b ?? "")

// questionNumber is text, not a number -- a plain string compare would sort
// "9" after "44". Non-numeric/null values sort like an empty string would
// (first ascending, last descending, via the sortDirection-driven reverse()
// below), matching how the other nullable text columns in this table behave.
const compareQuestionNumbers = (a: string | null, b: string | null) => {
  const aNum = a !== null && Number.isFinite(Number(a)) ? Number(a) : null
  const bNum = b !== null && Number.isFinite(Number(b)) ? Number(b) : null
  if (aNum === null && bNum === null) return 0
  if (aNum === null) return -1
  if (bNum === null) return 1
  return aNum - bNum
}

const escapeCsvField = (value: string) => {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

const downloadTestingProgressCsv = (rows: StudentTestingProgressRow[]) => {
  const headers = ["Student", "Student ID", "Status", "Test", "Question #", "Last Updated"]
  const lines = [
    headers.join(","),
    ...rows.map((row) =>
      [
        `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim() || "Unknown student",
        row.studentId,
        TestStatuses[row.status] ?? row.status,
        row.subject ? capitalize(row.subject) : "",
        row.questionNumber ?? "",
        format(new Date(row.updatedAt), "MMM d, h:mm a"),
      ]
        .map((field) => escapeCsvField(String(field)))
        .join(",")
    ),
  ]

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = `map-testing-progress-${format(new Date(), "yyyy-MM-dd")}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const TestingProgressSortableHeader = ({
  label,
  field,
  activeField,
  direction,
  onSort,
}: {
  label: string
  field: TestingProgressSortField
  activeField: TestingProgressSortField
  direction: "asc" | "desc"
  onSort: (field: TestingProgressSortField) => void
}) => {
  const isActive = activeField === field
  return (
    <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
      <button
        type="button"
        onClick={() => onSort(field)}
        className={clsx(
          "flex items-center gap-1 hover:text-gray-700 focus:outline-none",
          isActive && "text-gray-700"
        )}
      >
        {label}
        <span className={clsx("text-xs", !isActive && "text-gray-300")}>
          {isActive ? (direction === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  )
}

const TESTING_PROGRESS_PAGE_SIZE_OPTIONS = [10, 25, 50]
const DEFAULT_TESTING_PROGRESS_PAGE_SIZE = 10

export const TestingProgress = () => {
  const [rows] = useQuery(getStudentTestingProgress, {})
  const [search, setSearch] = useState("")
  const [sortField, setSortField] = useState<TestingProgressSortField>("student")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_TESTING_PROGRESS_PAGE_SIZE)

  const handleSort = (field: TestingProgressSortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
    setPage(1)
  }

  const filteredRows = useMemo(() => {
    const trimmedSearch = search.trim().toLowerCase()
    if (!trimmedSearch) return rows ?? []
    return (rows ?? []).filter((row) =>
      `${row.firstName ?? ""} ${row.lastName ?? ""}`.toLowerCase().includes(trimmedSearch)
    )
  }, [rows, search])

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows].sort((a, b) => {
      switch (sortField) {
        case "student":
          return (
            compareNullableStrings(a.lastName, b.lastName) ||
            compareNullableStrings(a.firstName, b.firstName)
          )
        case "status":
          return compareNullableStrings(a.status, b.status)
        case "subject":
          return compareNullableStrings(a.subject, b.subject)
        case "questionNumber":
          return compareQuestionNumbers(a.questionNumber, b.questionNumber)
        case "updatedAt":
          return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
        default:
          return 0
      }
    })
    return sortDirection === "asc" ? sorted : sorted.reverse()
  }, [filteredRows, sortField, sortDirection])

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / perPage))
  const currentPage = Math.min(page, totalPages)

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * perPage
    return sortedRows.slice(start, start + perPage)
  }, [sortedRows, currentPage, perPage])

  if (!rows || rows.length === 0) {
    return <div className="text-sm text-gray-500">No testing activity recorded yet.</div>
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div className="relative rounded-md shadow-sm sm:w-64">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <HiSearch className="w-4 h-4 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full py-1.5 pl-9 text-sm rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="Find a student"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
          />
        </div>
        <button
          type="button"
          onClick={() => downloadTestingProgressCsv(sortedRows)}
          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 uppercase bg-gray-200 rounded-lg hover:bg-gray-300"
        >
          <HiDownload className="w-4 h-4 mr-1.5" />
          Download Progress
        </button>
      </div>
      {sortedRows.length === 0 ? (
        <div className="text-sm text-gray-500">
          No student matching &quot;{search.trim()}&quot; found.
        </div>
      ) : (
        <div className="overflow-x-auto bg-white shadow sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <TestingProgressSortableHeader
                  label="Student"
                  field="student"
                  activeField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <TestingProgressSortableHeader
                  label="Status"
                  field="status"
                  activeField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <TestingProgressSortableHeader
                  label="Test"
                  field="subject"
                  activeField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <TestingProgressSortableHeader
                  label="Question #"
                  field="questionNumber"
                  activeField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <TestingProgressSortableHeader
                  label="Last Updated"
                  field="updatedAt"
                  activeField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pagedRows.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                    {`${row.firstName ?? ""} ${row.lastName ?? ""}`.trim() || "Unknown student"}{" "}
                    <span className="text-gray-400">({row.studentId})</span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <TestingProgressBadge status={row.status} />
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-500">
                    {row.subject ? capitalize(row.subject) : "—"}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-500">{row.questionNumber ?? "—"}</td>
                  <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                    {format(new Date(row.updatedAt), "MMM d, h:mm a")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                Rows per page
                <select
                  className="rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value))
                    setPage(1)
                  }}
                >
                  {TESTING_PROGRESS_PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * perPage + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * perPage, sortedRows.length)}
                </span>{" "}
                of <span className="font-medium">{sortedRows.length}</span> results
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={clsx(
                  "px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50",
                  currentPage === 1 && "opacity-50 pointer-events-none"
                )}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={clsx(
                  "px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50",
                  currentPage === totalPages && "opacity-50 pointer-events-none"
                )}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function SubscriptionStatus() {
  const currentUser = useCurrentUser()
  const isSubscribed = Boolean(currentUser?.organization.mapTestingSubscriptionId)
  const [subscriptionDetails] = useQuery(
    getSubscriptionDetails,
    {
      mapTestingSubscriptionId: currentUser?.organization?.mapTestingSubscriptionId!,
    },
    { enabled: isSubscribed && currentUser?.organization !== null }
  )
  const renewalDate = subscriptionDetails ? new Date(subscriptionDetails.renewalDate) : null

  const [testCredits] = useQuery(getTestCredits, {})

  return (
    <div className="bg-white shadow sm:rounded-lg h-full">
      <div className="px-4 py-5 sm:p-6">
        {isSubscribed ? (
          <>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              <HiCheckCircle className="inline mb-1 text-green-500" /> Subscribed to MAP Testing
            </h3>
            <div className="mt-2 sm:flex sm:items-start sm:justify-between">
              <div className="max-w-xl text-sm leading-5 text-gray-500">
                {testCredits && <p>You have {testCredits} test credits available</p>}
              </div>
            </div>
            {renewalDate && (
              <div className="mt-2 sm:flex sm:items-start sm:justify-between">
                <div className="max-w-xl text-sm leading-5 text-gray-500">
                  Credits Expire: {renewalDate.toLocaleDateString()}
                </div>
                <div className="mt-5 sm:mt-0 sm:ml-6 sm:flex-shrink-0 sm:flex sm:items-center">
                  <ButtonLink href="/map-testing/subscriptions">Manage</ButtonLink>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Check Out a Testing Subscription!
            </h3>
            <div className="mt-2 sm:flex sm:items-start sm:justify-between">
              <div className="max-w-xl text-sm leading-5 text-gray-500">
                <p>
                  The most affordable way to track your children’s academic growth throughout the
                  school year.
                </p>
              </div>
              <div className="mt-5 sm:mt-0 sm:ml-6 sm:flex-shrink-0 sm:flex sm:items-center">
                <ButtonLink href="/map-testing/subscriptions">View Plan Options</ButtonLink>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function ReadingFluency() {
  return (
    <div className="bg-white shadow sm:rounded-lg h-full">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900">Reading Fluency</h3>
        <div className="mt-2 sm:flex sm:items-start sm:justify-between">
          <div className="max-w-xl text-sm leading-5 text-gray-500">
            <p>
              MAP Reading Fluency assesses reading ability in grades K-5 in as little as 20 minutes.
            </p>
          </div>
          <div className="mt-5 sm:mt-0 sm:ml-6 sm:flex-shrink-0 sm:flex sm:items-center">
            <ButtonLink href="/map-fluency">Learn more</ButtonLink>
          </div>
        </div>
      </div>
    </div>
  )
}

function Mindprint() {
  return (
    <div className="bg-white shadow sm:rounded-lg h-full">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900">Mindprint</h3>
        <div className="mt-2 sm:flex sm:items-start sm:justify-between">
          <div className="max-w-xl text-sm leading-5 text-gray-500">
            <p>Unlock Your Child’s Potential with Mindprint</p>
          </div>
          <div className="mt-5 sm:mt-0 sm:ml-6 sm:flex-shrink-0 sm:flex sm:items-center">
            <ButtonLink href="/mindprint">Learn more</ButtonLink>
          </div>
        </div>
      </div>
    </div>
  )
}

function CertificatesLink() {
  return (
    <div className="bg-white shadow sm:rounded-lg h-full">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Get Your End of Year Certificate!
        </h3>
        <div className="mt-2 sm:flex sm:items-start sm:justify-between">
          <div className="max-w-xl text-sm leading-5 text-gray-500">
            <p>Create your own printable testing certificate.</p>
          </div>
          <div className="mt-5 sm:mt-0 sm:ml-6 sm:flex-shrink-0 sm:flex sm:items-center">
            <ButtonLink href="/certificate-generator">Create Certificate</ButtonLink>
          </div>
        </div>
      </div>
    </div>
  )
}

const NewTestResultsPage: BlitzPage = () => {
  return (
    <div className="px-4 pt-2 pb-6 space-y-10 md:py-6 sm:px-6 lg:px-8">
      <PageHeader title="Map Testing"></PageHeader>
      <Suspense fallback={<Loading />}>
        <div className="flex flex-col lg:flex-row justify-between gap-4">
          <div className="w-full lg:w-1/2">
            <SubscriptionStatus />
          </div>
          <div className="w-full lg:w-1/2">
            <ReadingFluency />
          </div>
          <div className="w-full lg:w-1/2">
            <Mindprint />
          </div>
          {/* <div className="w-full lg:w-1/3">
            <CertificatesLink />
          </div> */}
        </div>
        <div className="space-y-4" style={{ margin: "4rem 0" }}>
          <SectionHeading>Testing Progress</SectionHeading>
          <TestingProgress />
        </div>
        <div className="space-y-4" style={{ margin: "4rem 0" }}>
          <SectionHeading>Upcoming Tests</SectionHeading>
          <UpcomingTesting />
        </div>
      </Suspense>
    </div>
  )
}

NewTestResultsPage.authenticate = true
NewTestResultsPage.getLayout = (page) => <AppLayout>{page}</AppLayout>

export default NewTestResultsPage
